import { Link } from "react-router-dom";
import { ArrowUpRight, Heart, Lock } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import DailyInsight from "@/components/DailyInsight";
import { TOPICS } from "@/lib/topics";

const HERO_IMG = "https://images.unsplash.com/photo-1759264244741-7175af0b7e75?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NjZ8MHwxfHNlYXJjaHwzfHxib3V0aXF1ZSUyMG9mZmljZSUyMGRlc2t8ZW58MHx8fHwxNzgxMzkyNjY2fDA&ixlib=rb-4.1.0&q=85";
const FAMILY_IMG = "https://images.unsplash.com/photo-1637942189191-fa7add81c1bb?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzR8MHwxfHNlYXJjaHw0fHx3YXJtJTIwZmFtaWx5JTIwaG9tZXxlbnwwfHx8fDE3ODEzOTI2NjZ8MA&ixlib=rb-4.1.0&q=85";

const accentText = (a) => ({
  oxblood: "text-oxblood",
  gold: "text-gold",
  cognac: "text-cognac",
  terracotta: "text-terracotta",
}[a] || "text-oxblood");

export default function Home() {
  return (
    <div className="min-h-screen bg-ivory text-oxblood">
      <Navbar />

      {/* HERO */}
      <section className="relative px-6 md:px-12 pt-12 md:pt-20 pb-20 md:pb-28 overflow-hidden">
        <div className="max-w-6xl mx-auto grid md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-7 stagger">
            <span className="label-overline" data-testid="hero-overline">Free Consultations · No strings</span>
            <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl text-oxblood mt-6 leading-[1.02] tracking-tight" data-testid="hero-heading">
              Money, made <span className="italic text-cognac">gentle.</span>
              <br />
              Decisions, made <span className="italic text-gold">yours.</span>
            </h1>
            <p className="mt-8 text-lg md:text-xl text-inkSoft leading-relaxed max-w-xl">
              I'm Aisha — a financial advisor offering complimentary one-on-one consultations.
              Whether you're stuck on the paycheque treadmill, saving for a first home, or
              just want to understand how money <em>actually</em> works — let's talk.
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-4">
              <a href="#topics" className="btn-primary" data-testid="hero-explore-button">
                Explore topics <ArrowUpRight size={14} />
              </a>
              <Link to="/book" className="btn-ghost" data-testid="hero-book-button">
                Book a consultation
              </Link>
            </div>
            <div className="mt-10 flex items-center gap-6 text-sm text-inkMute">
              <span className="flex items-center gap-2" data-testid="badge-free"><Heart size={14} className="text-terracotta" /> 100% free</span>
              <span className="flex items-center gap-2" data-testid="badge-private"><Lock size={14} className="text-gold" /> Private & confidential</span>
            </div>
          </div>

          <div className="md:col-span-5 relative animate-fade-in">
            <div className="relative aspect-[4/5] overflow-hidden">
              <img src={HERO_IMG} alt="A warm boutique advisor's desk" className="w-full h-full object-cover" data-testid="hero-image" />
              <div className="absolute inset-0 bg-gradient-to-t from-oxblood/40 via-transparent to-transparent" />
            </div>
            <div className="absolute -bottom-6 -left-6 bg-ivory border border-gold/40 px-6 py-5 max-w-xs hidden md:block">
              <p className="label-overline">A note from Aisha</p>
              <p className="font-serif italic text-cognac text-lg mt-2 leading-snug">
                "There is no question too small. Bring your whole story."
              </p>
            </div>
          </div>
        </div>
        {/* subtle gold rule */}
        <div className="gold-rule max-w-6xl mx-auto mt-20" />
      </section>

      {/* DAILY INSIGHT */}
      <DailyInsight />

      {/* TOPICS BENTO */}
      <section id="topics" className="px-6 md:px-12 py-20 md:py-28">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
            <div>
              <span className="label-overline">Choose your starting point</span>
              <h2 className="font-serif text-4xl md:text-5xl text-oxblood mt-3 tracking-tight">
                What would you like to <span className="italic text-cognac">untangle?</span>
              </h2>
            </div>
            <p className="text-inkSoft max-w-md md:text-right">
              Every consultation is shaped around you. Pick the topic that feels closest —
              you'll tell me the rest in the next step.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5 stagger">
            {TOPICS.map((t) => {
              const Icon = t.icon;
              return (
                <Link
                  key={t.id}
                  to={`/book/${t.id}`}
                  className={`card-soft p-7 md:p-8 flex flex-col justify-between min-h-[200px] md:min-h-[220px] ${t.span || ""}`}
                  data-testid={`topic-card-${t.id}`}
                >
                  <Icon size={28} strokeWidth={1.5} className={accentText(t.accent)} />
                  <div className="mt-8">
                    <h3 className="font-serif text-2xl md:text-[26px] text-oxblood leading-tight">
                      {t.title}
                    </h3>
                    <p className="text-sm text-inkSoft/90 mt-3 leading-relaxed">{t.blurb}</p>
                    <span className="inline-flex items-center gap-2 mt-5 text-[11px] tracking-[0.22em] uppercase text-cognac group-hover:text-gold">
                      Start here <ArrowUpRight size={12} />
                    </span>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="how" className="px-6 md:px-12 py-20 bg-cream/60 border-y border-cognac/10">
        <div className="max-w-6xl mx-auto grid md:grid-cols-12 gap-12 items-center">
          <div className="md:col-span-5 relative">
            <img src={FAMILY_IMG} alt="A warm home" className="w-full aspect-[4/5] object-cover" data-testid="how-image" />
          </div>
          <div className="md:col-span-7">
            <span className="label-overline">How it works</span>
            <h2 className="font-serif text-4xl md:text-5xl text-oxblood mt-3 tracking-tight">
              Four steps. <span className="italic text-cognac">No pressure.</span>
            </h2>
            <ol className="mt-10 space-y-7">
              {[
                ["01", "Pick a topic", "Choose what's on your mind — or tell me in your own words."],
                ["02", "Share your situation", "A few brief details so we don't waste a single minute."],
                ["03", "Add your info", "Just the essentials. Everything is kept confidential."],
                ["04", "Book — free", "Choose a date, in-person or virtual. A calendar invite lands in your inbox."],
              ].map(([n, title, desc]) => (
                <li key={n} className="flex gap-6 items-start" data-testid={`step-${n}`}>
                  <span className="font-serif text-3xl text-gold w-12 shrink-0">{n}</span>
                  <div>
                    <h4 className="font-serif text-2xl text-oxblood">{title}</h4>
                    <p className="text-inkSoft mt-1">{desc}</p>
                  </div>
                </li>
              ))}
            </ol>
            <Link to="/book" className="btn-primary mt-12" data-testid="how-book-button">
              Book my free consultation <ArrowUpRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
